import React, { useState, useMemo, useRef, useEffect, useCallback, useDeferredValue } from "react";
import Markdown from "react-markdown";
import { GoogleGenAI, Type } from "@google/genai";
import { Shot, RateCard, TaskType, ProjectSetup, ProjectDocument, ShotStatus } from "../types";
import type { Scene } from "../models/production";
import { formatCurrency, cn, getEpisodeFromShotCode, getSequenceFromShotCode, sortShots } from "../utils";
import { parseShotCodeBreadcrumb, validateShotCode, validateShotCodeAgainstBreadcrumb } from "../shared/shotCodeParser";
import { inferSequenceNumberFromScene, normalizeScriptSceneInput } from "../shared/sceneMappers";
import { toast } from "sonner";
import { Plus, Copy, Trash2, Search, CheckSquare, Square, Image as ImageIcon, Edit3, GripVertical, ArrowUpDown, FileText, Sparkles, X, Filter, FilterX, ChevronDown, ChevronUp, ChevronRight, FoldVertical, Film, AlertTriangle } from "lucide-react";
import { BulkEditModal } from "./BulkEditModal";
import { ShotDetailModal } from "./ShotDetailModal";
import { ShotCodeGenerator } from "./ShotCodeGenerator";
import { SectionPillHeader } from "./SectionPillHeader";
import { BID_ITEM_STATUSES, getBidStatusClasses, getBidStatusValue, isBidItemIncluded, isExcludedBidStatus } from "../lib/bidStatus";

interface FilterCriterion {
  id: string;
  field: string;
  operator: 'contains' | 'not_contains' | 'equals' | 'not_equals' | 'gt' | 'lt' | 'gte' | 'lte' | 'is_empty' | 'is_not_empty';
  value: string;
}

const FILTER_FIELDS = [
  { label: 'Shot Code', value: 'shotCode' },
  { label: 'Description', value: 'description' },
  { label: 'Scene Description', value: 'sceneDescription' },
  { label: 'Scope of Work', value: 'scopeOfWork' },
  { label: 'Status', value: 'status' },
  { label: 'Total Days', value: 'totalDays' },
  { label: 'Total Cost', value: 'totalCost' },
  { label: 'Working Duration', value: 'frames' },
  { label: 'Quantity', value: 'quantity' },
  { label: 'Episode', value: 'episode' },
  { label: 'Sequence', value: 'sequence' },
  { label: 'Concept Days', value: 'days.concept' },
  { label: 'DMP Days', value: 'days.dmp' },
  { label: 'Comp Days', value: 'days.comp' },
  { label: 'Roto Days', value: 'days.roto' },
  { label: 'Prep Days', value: 'days.prep' },
  { label: 'GFX Days', value: 'days.gfx' },
  { label: 'Camera TR Days', value: 'days.cameraTR' },
  { label: 'Object TR Days', value: 'days.objectTR' },
  { label: 'R-Anim Days', value: 'days.rANIM' },
  { label: 'Layout Days', value: 'days.layout' },
  { label: 'Anim Days', value: 'days.anim' },
  { label: 'Light Days', value: 'days.light' },
  { label: 'FX Days', value: 'days.fx' },
];

function formatShotCodeWarningMessage(shotCode: string, errors: string[], breadcrumb?: string | null) {
  const parts = shotCode.trim().split("_");
  const parsedBreadcrumb = parseShotCodeBreadcrumb(breadcrumb);
  const sequenceIndex = parsedBreadcrumb?.includeEpisode ? 2 : 1;
  const shotIndex = parsedBreadcrumb?.includeEpisode ? 3 : 2;
  const details = errors.map((error) => {
    if (error === "sequenceNumber must be 3 characters") {
      const currentValue = parts[sequenceIndex] || "";
      return `Sequence segment "${currentValue}" should be 3 digits, for example 003 or 028.`;
    }
    if (error === "shotNumber must be 4 characters" && /^\d{3}$/.test(parts[shotIndex] || "")) {
      return "";
    }
    if (error === "shotNumber must be 4 characters") {
      const currentValue = parts[shotIndex] || "";
      return `Shot segment "${currentValue}" should use the project shot format.`;
    }
    if (error.startsWith("Expected ")) {
      return `Shot code should follow the project format: ${parsedBreadcrumb?.includeEpisode ? "show_episode_sequence_shot" : "show_sequence_shot"}.`;
    }
    return error;
  }).filter(Boolean);

  return details.join(" ");
}

const OPERATORS = [
  { label: 'Contains', value: 'contains' },
  { label: 'Not Contains', value: 'not_contains' },
  { label: 'Equals', value: 'equals' },
  { label: 'Not Equals', value: 'not_equals' },
  { label: 'Greater Than', value: 'gt' },
  { label: 'Less Than', value: 'lt' },
  { label: 'Greater Than or Equal', value: 'gte' },
  { label: 'Less Than or Equal', value: 'lte' },
  { label: 'Is Empty', value: 'is_empty' },
  { label: 'Is Not Empty', value: 'is_not_empty' },
];
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ExportFilterState } from "../lib/bidVersioning";

function parseSceneHeadingParts(value?: string) {
  const heading = value?.trim();
  if (!heading) {
    return {
      intExt: undefined as string | undefined,
      locationName: undefined as string | undefined,
      dayNight: undefined as string | undefined,
      description: undefined as string | undefined,
    };
  }

  const matched = heading.match(/^(INT(?:\/EXT)?|EXT(?:\/INT)?)[\.\s-]*(.*?)(?:\s+-\s+(DAY|NIGHT|DUSK|DAWN).*)?$/i);
  if (!matched) {
    return {
      intExt: undefined,
      locationName: heading,
      dayNight: undefined,
      description: heading,
    };
  }

  return {
    intExt: matched[1]?.replace(/\.$/, "").toUpperCase(),
    locationName: matched[2]?.trim() || undefined,
    dayNight: matched[3]?.toUpperCase(),
    description: heading,
  };
}

function mapPersistedSceneEntry(entry: unknown, index: number) {
  if (entry && typeof entry === "object") {
    return normalizeScriptSceneInput(entry as any);
  }

  const value = String(entry || "").trim();
  if (!value) return null;

  const matched = value.match(/^([^:]+):\s*(.*)$/);
  const sceneNumber = matched?.[1]?.trim() || String(index + 1);
  const heading = matched?.[2]?.trim() || value;
  const headingParts = parseSceneHeadingParts(heading);

  return normalizeScriptSceneInput({
    sceneNumber,
    locationName: headingParts.locationName,
    intExt: headingParts.intExt,
    dayNight: headingParts.dayNight,
    description: headingParts.description,
  });
}

function ResizableTh({
  id,
  width,
  onResizeStart,
  children,
  className,
  title,
  style,
}: {
  id: string;
  width: number;
  onResizeStart: (e: React.MouseEvent, id: string) => void;
  children: React.ReactNode;
  className?: string;
  title?: string;
  style?: React.CSSProperties;
}) {
  return (
    <th
      className={cn("relative group", className)}
      style={{ width, minWidth: width, maxWidth: width, ...style }}
      title={title}
    >
      {children}
      <div
        className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-indigo-400 active:bg-indigo-600 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
        onMouseDown={(e) => onResizeStart(e, id)}
      />
    </th>
  );
}

function AutoResizeTextarea({
  value,
  onChange,
  onKeyDown,
  placeholder,
  readOnly,
  className,
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  readOnly?: boolean;
  className?: string;
}) {
  const [localValue, setLocalValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [localValue, isEditing]);

  const handleBlur = () => {
    setIsEditing(false);
    if (localValue !== value) {
      onChange({ target: { value: localValue } } as any);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLocalValue(e.target.value);
  };

  if (!isEditing && !readOnly) {
    return (
      <div 
        onClick={() => setIsEditing(true)}
        className={cn("cursor-text min-h-[24px] p-1 text-xs leading-normal", className)}
      >
        {localValue ? (
          <div className="markdown-body prose-sm max-w-none prose-p:my-0 prose-ul:my-0 prose-li:my-0">
            <Markdown>{localValue}</Markdown>
          </div>
        ) : (
          <span className="text-slate-400">{placeholder}</span>
        )}
      </div>
    );
  }

  if (readOnly) {
    return (
      <div className={cn("min-h-[24px] p-1 text-xs leading-normal", className)}>
        {localValue ? (
          <div className="markdown-body prose-sm max-w-none prose-p:my-0 prose-ul:my-0 prose-li:my-0">
            <Markdown>{localValue}</Markdown>
          </div>
        ) : (
          <span className="text-slate-400">{placeholder}</span>
        )}
      </div>
    );
  }

  return (
    <textarea
      ref={textareaRef}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      readOnly={readOnly}
      rows={1}
      autoFocus
      className={cn("overflow-hidden resize-none break-words", className)}
    />
  );
}

interface Props {
  shots: Shot[];
  rates: RateCard;
  setup: ProjectSetup;
  onChange: (shots: Shot[] | ((prev: Shot[]) => Shot[])) => void;
  onAddShot: () => void;
  onDuplicateShot: (id: string) => void;
  onDeleteShot: (id: string) => void;
  onUpdateSetup: (updates: Partial<ProjectSetup>) => void;
  readOnly?: boolean;
  documents?: ProjectDocument[];
  scenes?: Scene[];
  onFilterStateChange?: (filterState: ExportFilterState) => void;
  importRevision?: number;
}

interface SortableRowProps {
  shot: Shot;
  index: number;
  calculateShotTotal: (shot: Shot) => number;
  isSelected: boolean;
  toggleSelect: (id: string) => void;
  handleShotChange: (id: string, field: keyof Shot, value: any) => void;
  handleDaysChange: (id: string, task: TaskType, value: string) => void;
  handleThumbnailUpload: (id: string, e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRowDoubleClick: (shot: Shot, e: React.MouseEvent) => void;
  handleTextareaKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>, id: string, field: keyof Shot, currentValue: string) => void;
  onDuplicateShot: (id: string) => void;
  onDeleteShot: (id: string) => void;
  readOnly: boolean;
  colWidths: Record<string, number>;
  setup: ProjectSetup;
  rates: RateCard;
  biddingMode: boolean;
}

function getShotCodeWarningForBreadcrumb(shotCode: string, breadcrumb?: string | null): string | null {
  const breadcrumbValidation = validateShotCodeAgainstBreadcrumb(shotCode, breadcrumb);
  const fallbackValidation = validateShotCode(shotCode);
  const relevantShotCodeErrors = breadcrumbValidation
    ? breadcrumbValidation.errors
    : fallbackValidation.errors.filter((error) => {
        const shotCodeParts = shotCode.trim().split("_");
        if (error === "shotNumber must be 4 characters" && shotCodeParts.length === 4 && /^\d{3}$/.test(shotCodeParts[3] || "")) {
          return false;
        }
        return true;
      });

  return relevantShotCodeErrors.length > 0
    ? formatShotCodeWarningMessage(shotCode, relevantShotCodeErrors, breadcrumb)
    : null;
}

const SortableRow = React.memo(({
  shot,
  index,
  calculateShotTotal,
  isSelected,
  toggleSelect,
  handleShotChange,
  handleDaysChange,
  handleThumbnailUpload,
  handleRowDoubleClick,
  handleTextareaKeyDown,
  onDuplicateShot,
  onDeleteShot,
  readOnly,
  colWidths,
  setup,
  rates,
  biddingMode,
}: SortableRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: shot.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    position: isDragging ? 'relative' as const : undefined,
  };

  const shotTotal = calculateShotTotal(shot);
  const shotCodeWarning = getShotCodeWarningForBreadcrumb(shot.shotCode, setup.shotCodeBreadcrumb);

  return (
    <tr
      ref={setNodeRef}
      style={style}
      onDoubleClick={(e) => handleRowDoubleClick(shot, e)}
      className={cn(
        "transition-colors group cursor-default bg-white dark:bg-slate-900 overflow-hidden hover:bg-slate-50 dark:hover:bg-slate-800/70",
        isSelected && "bg-indigo-50/50 dark:bg-indigo-900/20",
        (isExcludedBidStatus(shot.status) || (!shot.status && !shot.active)) && "opacity-50",
        isDragging && "bg-white dark:bg-slate-850 shadow-lg opacity-100 ring-2 ring-indigo-500 ring-inset",
        biddingMode && "h-[56px]"
      )}
    >
      <td className={cn("px-2 align-middle sticky left-0 z-[1] bg-inherit border-l-2 border-r-2 border-b-2 border-white dark:border-slate-900", biddingMode ? "py-1" : "py-2")} style={{ left: 0 }}>
        <div className="relative flex items-center justify-center min-h-[20px] pl-5">
          {!readOnly && (
            <div
              {...attributes}
              {...listeners}
              className="absolute left-0 cursor-grab active:cursor-grabbing p-1 text-slate-300 hover:text-slate-500 dark:text-slate-600 dark:hover:text-slate-300 transition-colors"
            >
              <GripVertical className={cn("w-4 h-4", biddingMode && "w-3 h-3")} />
            </div>
          )}
          <button onClick={() => toggleSelect(shot.id)} className="flex items-center justify-center text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-200" disabled={readOnly}>
            {isSelected ? <CheckSquare className={cn("w-4 h-4 text-indigo-600", biddingMode && "w-3 h-3")} /> : <Square className={cn("w-4 h-4", biddingMode && "w-3 h-3")} />}
          </button>
        </div>
      </td>
      <td className={cn("px-4 text-center align-middle sticky z-[1] bg-inherit border-r-2 border-b-2 border-white dark:border-slate-900", biddingMode ? "py-1" : "py-2")} style={{ left: colWidths.select }}>
        <select
          value={getBidStatusValue(shot)}
          onChange={(e) => handleShotChange(shot.id, "status", e.target.value)}
          disabled={readOnly}
          className={cn(
            "w-full px-1 py-1 text-[10px] font-bold uppercase tracking-wider border rounded focus:ring-1 focus:ring-indigo-500 outline-none transition-colors",
            getBidStatusClasses(getBidStatusValue(shot))
          )}
        >
          {BID_ITEM_STATUSES.map((status) => (
            <option key={status} value={status}>{status}</option>
          ))}
        </select>
      </td>
      <td className={cn("px-4 text-center align-middle sticky z-[1] bg-inherit border-r-2 border-b-2 border-white dark:border-slate-900", biddingMode ? "py-1" : "py-2")} style={{ left: colWidths.select + colWidths.active }}>
        <div 
          onClick={() => !readOnly && document.getElementById(`thumb-upload-${shot.id}`)?.click()}
          className={cn(
          "relative flex items-center justify-center bg-slate-100 dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 overflow-hidden ml-1 group/thumb", 
          biddingMode ? "w-[72px] h-[48px]" : "w-[106px] h-[70px]",
          !readOnly && "cursor-pointer hover:bg-slate-200 transition-colors"
        )}>
          {shot.thumbnail ? (
            <img src={shot.thumbnail} alt="thumb" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon className={cn("text-slate-400", biddingMode ? "w-3 h-3" : "w-4 h-4")} />
          )}
          {!readOnly && (
            <>
              <input
                id={`thumb-upload-${shot.id}`}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleThumbnailUpload(shot.id, e)}
              />
            </>
          )}
        </div>
      </td>
      <td className={cn("px-4 align-middle sticky z-[1] bg-inherit border-r-2 border-b-2 border-white dark:border-slate-900", biddingMode ? "py-1" : "py-2")} style={{ left: colWidths.select + colWidths.active + colWidths.thumb }}>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={shot.shotCode}
            onChange={(e) => handleShotChange(shot.id, "shotCode", e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.blur();
              }
            }}
            placeholder="SH_0010"
            readOnly={readOnly}
            className={cn(
              "w-full bg-transparent border-none p-1 focus:ring-1 focus:ring-indigo-500 rounded outline-none font-sans leading-normal disabled:opacity-75 text-xs",
              shotCodeWarning && "text-amber-700 dark:text-amber-300"
            )}
          />
          {shotCodeWarning && (
            <span
              className="inline-flex shrink-0 items-center justify-center rounded-md bg-amber-50 p-1 text-amber-600 dark:bg-amber-950/40 dark:text-amber-300"
              title={`Shot code format warning: ${shotCodeWarning}`}
            >
              <AlertTriangle className="h-3.5 w-3.5" />
            </span>
          )}
        </div>
      </td>
      <td className={cn(
        "px-4 text-right font-sans text-slate-900 dark:text-slate-100 align-middle sticky z-[1] bg-inherit border-r-2 border-b-2 border-white dark:border-slate-900 text-xs",
        biddingMode ? "py-1" : "py-2"
      )} style={{ left: colWidths.select + colWidths.active + colWidths.thumb + colWidths.shotCode }}>
        <div>{formatCurrency(shotTotal, setup.currency)}</div>
        {setup.targetCurrency && setup.exchangeRate > 0 && (
          <div className="text-slate-400 dark:text-slate-500 mt-0.5 text-xs">
            {formatCurrency(shotTotal * setup.exchangeRate, setup.targetCurrency)}
          </div>
        )}
      </td>
      {!biddingMode && (
        <>
          <td className="px-4 py-2 align-middle border-r-2 border-b-2 border-white dark:border-slate-900">
            <AutoResizeTextarea
              value={shot.description}
              onChange={(e) => handleShotChange(shot.id, "description", e.target.value)}
              onKeyDown={(e) => handleTextareaKeyDown(e, shot.id, "description", shot.description)}
              placeholder="Description..."
              readOnly={readOnly}
              className="w-full bg-transparent border-none p-1 focus:ring-1 focus:ring-indigo-500 rounded outline-none text-xs leading-normal disabled:opacity-75"
            />
          </td>
          <td className="px-2 py-2 align-middle border-r-2 border-b-2 border-white dark:border-slate-900">
            <input
              type="number"
              min="0"
              step="1"
              value={shot.frames || 0}
              onChange={(e) => handleShotChange(shot.id, "frames", parseInt(e.target.value) || 0)}
              readOnly={readOnly}
              className="w-full bg-transparent border border-transparent hover:border-slate-200 dark:hover:border-slate-600 focus:border-indigo-500 p-1 text-center rounded outline-none text-xs disabled:opacity-75"
            />
          </td>
          <td className="px-2 py-2 align-middle border-r-2 border-b-2 border-white dark:border-slate-900">
            <input
              type="number"
              min="1"
              step="1"
              value={shot.quantity || 1}
              onChange={(e) => handleShotChange(shot.id, "quantity", parseInt(e.target.value) || 1)}
              readOnly={readOnly}
              className="w-full bg-transparent border border-transparent hover:border-slate-200 dark:hover:border-slate-600 focus:border-indigo-500 p-1 text-center rounded outline-none text-xs disabled:opacity-75"
            />
          </td>
        </>
      )}
      {(["supervisor", "concept", "dmp", "comp", "roto", "prep", "gfx", "cameraTR", "objectTR", "rANIM", "layout", "anim", "light", "fx"] as TaskType[]).map((task, i) => {
        let displayValue: number | string = shot.days[task] || "";
        let isReadOnly = readOnly;

        if (task === "supervisor") {
          const otherDays = 
            (shot.days.concept || 0) +
            (shot.days.dmp || 0) +
            (shot.days.comp || 0) +
            (shot.days.roto || 0) +
            (shot.days.prep || 0) +
            (shot.days.gfx || 0) +
            (shot.days.cameraTR || 0) +
            (shot.days.objectTR || 0) +
            (shot.days.rANIM || 0) +
            (shot.days.layout || 0) +
            (shot.days.anim || 0) +
            (shot.days.light || 0) +
            (shot.days.fx || 0);
          
          displayValue = (otherDays * ((setup.supervisionPercentage || 0) / 100)).toFixed(2);
          if (displayValue.endsWith(".00")) displayValue = displayValue.slice(0, -3);
          isReadOnly = true;
        }

        return (
          <td key={task} className={cn("px-1 bg-slate-50 dark:bg-slate-800/80 border-r-2 border-b-2 border-white dark:border-slate-900 align-middle", (i === 0 || (biddingMode && task === "concept")) && "border-l-2", biddingMode ? "py-1" : "py-2")}>
            <input
              type="number"
              min="0"
              step="0.5"
              value={displayValue}
              onChange={(e) => handleDaysChange(shot.id, task, e.target.value)}
              readOnly={isReadOnly}
              className="w-full bg-transparent border border-transparent hover:border-slate-200 dark:hover:border-slate-600 focus:border-indigo-500 p-1 text-center rounded outline-none disabled:opacity-75 text-xs"
            />
          </td>
        );
      })}
      {!biddingMode && (
        <>
          <td className={cn("px-4 align-middle border-r-2 border-b-2 border-white dark:border-slate-900", biddingMode ? "py-1" : "py-2")}>
              <AutoResizeTextarea
                value={shot.assumptions}
                onChange={(e) => handleShotChange(shot.id, "assumptions", e.target.value)}
                onKeyDown={(e) => handleTextareaKeyDown(e, shot.id, "assumptions", shot.assumptions)}
                placeholder="Assumptions..."
                readOnly={readOnly}
                className={cn(
                  "w-full bg-transparent border-none p-1 focus:ring-1 focus:ring-indigo-500 rounded outline-none leading-normal text-slate-500 dark:text-slate-400 disabled:opacity-75 text-xs",
                  biddingMode && "h-[40px] resize-none overflow-hidden"
                )}
              />
          </td>
          <td className={cn("px-4 align-middle border-r-2 border-b-2 border-white dark:border-slate-900", biddingMode ? "py-1" : "py-2")}>
              <AutoResizeTextarea
                value={shot.scopeOfWork || ""}
                onChange={(e) => handleShotChange(shot.id, "scopeOfWork", e.target.value)}
                onKeyDown={(e) => handleTextareaKeyDown(e, shot.id, "scopeOfWork", shot.scopeOfWork)}
                placeholder="Scope of work..."
                readOnly={readOnly}
                className={cn(
                  "w-full bg-transparent border-none p-1 focus:ring-1 focus:ring-indigo-500 rounded outline-none leading-normal text-slate-500 dark:text-slate-400 disabled:opacity-75 text-xs",
                  biddingMode && "h-[40px] resize-none overflow-hidden"
                )}
              />
          </td>
          <td className={cn("px-4 align-middle border-r-2 border-b-2 border-white dark:border-slate-900", biddingMode ? "py-1" : "py-2")}>
              <AutoResizeTextarea
                value={shot.internalNotes}
                onChange={(e) => handleShotChange(shot.id, "internalNotes", e.target.value)}
                onKeyDown={(e) => handleTextareaKeyDown(e, shot.id, "internalNotes", shot.internalNotes)}
                placeholder="Internal notes..."
                readOnly={readOnly}
                className={cn(
                  "w-full bg-transparent border-none p-1 focus:ring-1 focus:ring-indigo-500 rounded outline-none leading-normal text-slate-500 dark:text-slate-400 disabled:opacity-75 text-xs",
                  biddingMode && "h-[40px] resize-none overflow-hidden"
                )}
              />
          </td>
        </>
      )}
      {!readOnly && (
        <td className={cn("px-4 align-middle border-r-2 border-b-2 border-white dark:border-slate-900", biddingMode ? "py-1" : "py-2")}>
          <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => onDuplicateShot(shot.id)}
              className="p-1 text-slate-400 hover:text-indigo-600 rounded"
              title="Duplicate"
            >
              <Copy className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDeleteShot(shot.id)}
              className="p-1 text-slate-400 hover:text-red-600 rounded"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </td>
      )}
    </tr>
  );
});

export function ShotTable({
  shots,
  rates,
  setup,
  onChange,
  onAddShot,
  onDuplicateShot,
  onDeleteShot,
  onUpdateSetup,
  readOnly = false,
  documents = [],
  scenes = [],
  onFilterStateChange,
  importRevision = 0,
}: Props) {
  const [search, setSearch] = useState("");
  const [biddingMode, setBiddingMode] = useState(false);
  const totalCols = 5 + (biddingMode ? 0 : 4) + 13 + 3 + (readOnly ? 0 : 1);
  const deferredSearch = useDeferredValue(search);
  const deferredShots = useDeferredValue(shots);
  const [selectedStatuses, setSelectedStatuses] = useState<ShotStatus[]>(BID_ITEM_STATUSES);
  const [showCodeIssuesOnly, setShowCodeIssuesOnly] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState<FilterCriterion[]>([]);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [smartFilter, setSmartFilter] = useState("");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const statusFilterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = 0;
    }
  }, [biddingMode]);
  const [isSmartFiltering, setIsSmartFiltering] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [isCreatorOpen, setIsCreatorOpen] = useState(false);
  const [selectedShotIdForDetail, setSelectedShotIdForDetail] = useState<string | null>(null);
  const [collapsedEpisodes, setCollapsedEpisodes] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!importRevision) return;
    const episodes = new Set(
      deferredShots
        .map((shot) => getEpisodeFromShotCode(shot.shotCode) || "Unknown")
        .filter(Boolean)
    );
    setCollapsedEpisodes(episodes);
  }, [importRevision, deferredShots]);

  const toggleEpisodeCollapse = (episode: string) => {
    setCollapsedEpisodes(prev => {
      const next = new Set(prev);
      if (next.has(episode)) {
        next.delete(episode);
      } else {
        next.add(episode);
      }
      return next;
    });
  };

  const sensors = useMemo(() => [
    {
      sensor: PointerSensor,
      options: {
        activationConstraint: {
          distance: 8,
        },
      },
    },
    {
      sensor: KeyboardSensor,
      options: {
        coordinateGetter: sortableKeyboardCoordinates,
      },
    },
  ], []);

  const selectedShotForDetail = useMemo(() => {
    return deferredShots.find(s => s.id === selectedShotIdForDetail) || null;
  }, [deferredShots, selectedShotIdForDetail]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = shots.findIndex((s) => s.id === active.id);
      const newIndex = shots.findIndex((s) => s.id === over.id);
      onChange(arrayMove(shots, oldIndex, newIndex));
    }
  }, [shots, onChange]);

  const [colWidths, setColWidths] = useState<Record<string, number>>({
    select: 60,
    active: 105,
    thumb: 140,
    shotCode: 150,
    description: 200,
    scopeOfWork: 200,
    frames: 80,
    qty: 80,
    assumptions: 200,
    internalNotes: 200,
    supervisor: 75,
    concept: 75,
    dmp: 75,
    comp: 75,
    roto: 75,
    prep: 75,
    gfx: 75,
    cameraTR: 75,
    objectTR: 75,
    rANIM: 75,
    layout: 75,
    anim: 75,
    light: 75,
    fx: 75,
    total: 110,
    actions: 120
  });

  const [resizing, setResizing] = useState<{ col: string; startX: number; startWidth: number } | null>(null);

  useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const diff = e.pageX - resizing.startX;
      const newWidth = Math.max(40, resizing.startWidth + diff);
      setColWidths((prev) => ({ ...prev, [resizing.col]: newWidth }));
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [resizing]);

  const visibleWidth = useMemo(() => {
    let total = colWidths.select + colWidths.active + colWidths.thumb + colWidths.shotCode + colWidths.total + (readOnly ? 0 : colWidths.actions);
    
    if (!biddingMode) {
      total += colWidths.description + colWidths.scopeOfWork + colWidths.frames + colWidths.qty + colWidths.supervisor + colWidths.assumptions + colWidths.internalNotes;
    }
    
    const tasks: TaskType[] = ["concept", "dmp", "comp", "roto", "prep", "gfx", "cameraTR", "objectTR", "rANIM", "layout", "anim", "light", "fx"];
    tasks.forEach(t => {
      if (!(biddingMode && t === "supervisor")) {
        total += colWidths[t];
      }
    });
    
    return total;
  }, [colWidths, biddingMode, readOnly]);

  const handleResizeStart = (e: React.MouseEvent, col: string) => {
    e.preventDefault();
    setResizing({
      col,
      startX: e.pageX,
      startWidth: colWidths[col] || 100,
    });
  };

  const calculateShotTotal = useCallback((shot: Shot) => {
    const baseTaskCost =
      (shot.days.concept || 0) * (rates.concept || 0) +
      (shot.days.dmp || 0) * (rates.dmp || 0) +
      (shot.days.comp || 0) * (rates.comp || 0) +
      (shot.days.roto || 0) * (rates.roto || 0) +
      (shot.days.prep || 0) * (rates.prep || 0) +
      (shot.days.gfx || 0) * (rates.gfx || 0) +
      (shot.days.cameraTR || 0) * (rates.cameraTR || 0) +
      (shot.days.objectTR || 0) * (rates.objectTR || 0) +
      (shot.days.rANIM || 0) * (rates.rANIM || 0) +
      (shot.days.layout || 0) * (rates.layout || 0) +
      (shot.days.anim || 0) * (rates.anim || 0) +
      (shot.days.light || 0) * (rates.light || 0) +
      (shot.days.fx || 0) * (rates.fx || 0);
      
    const taskDays = 
      (shot.days.concept || 0) +
      (shot.days.dmp || 0) +
      (shot.days.comp || 0) +
      (shot.days.roto || 0) +
      (shot.days.prep || 0) +
      (shot.days.gfx || 0) +
      (shot.days.cameraTR || 0) +
      (shot.days.objectTR || 0) +
      (shot.days.rANIM || 0) +
      (shot.days.layout || 0) +
      (shot.days.anim || 0) +
      (shot.days.light || 0) +
      (shot.days.fx || 0);
    
    const supDays = taskDays * ((setup.supervisionPercentage || 0) / 100);
    const supCost = supDays * (rates.supervisor || 0);
    const taskCost = baseTaskCost + supCost;
    return taskCost * (shot.quantity || 1);
  }, [rates, setup.supervisionPercentage]);

  const getFieldValue = useCallback((shot: Shot, field: string) => {
    if (field === 'totalDays') {
      return Object.values(shot.days).reduce((a, b) => a + (b || 0), 0);
    }
    if (field === 'totalCost') {
      return calculateShotTotal(shot);
    }
    if (field === 'episode') {
      return getEpisodeFromShotCode(shot.shotCode);
    }
    if (field === 'sequence') {
      return getSequenceFromShotCode(shot.shotCode);
    }
    if (field.startsWith('days.')) {
      const task = field.split('.')[1] as TaskType;
      return shot.days[task] || 0;
    }
    return (shot as any)[field];
  }, [calculateShotTotal]);

  const selectedStatusSet = useMemo(() => new Set(selectedStatuses), [selectedStatuses]);
  const isStatusFilterActive = selectedStatuses.length < BID_ITEM_STATUSES.length;
  const invalidShotCodeCount = useMemo(
    () => deferredShots.filter((shot) => getShotCodeWarningForBreadcrumb(shot.shotCode, setup.shotCodeBreadcrumb)).length,
    [deferredShots, setup.shotCodeBreadcrumb]
  );

  const filteredShots = useMemo(() => {
    return deferredShots.filter((s) => {
      // Basic search
      const searchMatch = !deferredSearch || 
        s.shotCode.toLowerCase().includes(deferredSearch.toLowerCase()) ||
        s.description.toLowerCase().includes(deferredSearch.toLowerCase());
      
      if (!searchMatch) return false;

      const statusMatch = selectedStatusSet.has(getBidStatusValue(s));
      if (!statusMatch) return false;

      if (showCodeIssuesOnly && !getShotCodeWarningForBreadcrumb(s.shotCode, setup.shotCodeBreadcrumb)) {
        return false;
      }

      // Advanced filters
      if (advancedFilters.length === 0) return true;

      return advancedFilters.every((filter) => {
        const fieldValue = getFieldValue(s, filter.field);
        const filterValue = filter.value;

        switch (filter.operator) {
          case 'contains':
            return String(fieldValue).toLowerCase().includes(String(filterValue).toLowerCase());
          case 'not_contains':
            return !String(fieldValue).toLowerCase().includes(String(filterValue).toLowerCase());
          case 'equals':
            return String(fieldValue).toLowerCase() === String(filterValue).toLowerCase();
          case 'not_equals':
            return String(fieldValue).toLowerCase() !== String(filterValue).toLowerCase();
          case 'gt':
            return Number(fieldValue) > Number(filterValue);
          case 'lt':
            return Number(fieldValue) < Number(filterValue);
          case 'gte':
            return Number(fieldValue) >= Number(filterValue);
          case 'lte':
            return Number(fieldValue) <= Number(filterValue);
          case 'is_empty':
            return !fieldValue || fieldValue === '';
          case 'is_not_empty':
            return !!fieldValue && fieldValue !== '';
          default:
            return true;
        }
      });
    });
  }, [deferredShots, deferredSearch, advancedFilters, getFieldValue, selectedStatusSet, showCodeIssuesOnly, setup.shotCodeBreadcrumb]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target as Node)) {
        setIsStatusFilterOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSmartFilter = async () => {
    if (!smartFilter.trim() || !process.env.GEMINI_API_KEY) return;
    
    setIsSmartFiltering(true);
    try {
      const ai = new GoogleGenAI({ apiKey: process.env.GEMINI_API_KEY });
      const response = await ai.models.generateContent({
        model: "gemini-3-flash-preview",
        contents: `Convert this natural language filter query into a JSON array of filter criteria for a VFX shot list.
        Query: "${smartFilter}"
        
        Available fields: ${FILTER_FIELDS.map(f => f.value).join(', ')}
        Available operators: ${OPERATORS.map(o => o.value).join(', ')}
        
        Return ONLY a JSON array of objects with fields: { field, operator, value }.
        Example: "Active shots with > 10 days" -> [{"field": "active", "operator": "equals", "value": "true"}, {"field": "totalDays", "operator": "gt", "value": "10"}]`,
        config: {
          responseMimeType: "application/json",
        }
      });

      const result = JSON.parse(response.text);
      if (Array.isArray(result)) {
        const newFilters: FilterCriterion[] = result.map((f: any) => ({
          id: Math.random().toString(36).substr(2, 9),
          field: f.field,
          operator: f.operator,
          value: String(f.value),
        }));
        setAdvancedFilters(newFilters);
        setIsFilterPanelOpen(true);
        setSmartFilter("");
      }
    } catch (error) {
      console.error("Smart filter error:", error);
      toast.error("Failed to parse smart filter. Try a simpler query.");
    } finally {
      setIsSmartFiltering(false);
    }
  };

  const addFilter = () => {
    setAdvancedFilters([
      ...advancedFilters,
      { id: Math.random().toString(36).substr(2, 9), field: 'shotCode', operator: 'contains', value: '' }
    ]);
  };

  const removeFilter = (id: string) => {
    setAdvancedFilters(advancedFilters.filter(f => f.id !== id));
  };

  const updateFilter = (id: string, updates: Partial<FilterCriterion>) => {
    setAdvancedFilters(advancedFilters.map(f => f.id === id ? { ...f, ...updates } : f));
  };

  const clearFilters = () => {
    setAdvancedFilters([]);
    setSearch("");
    setSelectedStatuses(BID_ITEM_STATUSES);
    setShowCodeIssuesOnly(false);
  };

  const toggleStatusFilter = useCallback((status: ShotStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(status) ? prev.filter((value) => value !== status) : [...prev, status]
    );
  }, []);

  const selectAllStatuses = useCallback(() => {
    setSelectedStatuses(BID_ITEM_STATUSES);
  }, []);

  const handleNavigateDetail = (direction: 'prev' | 'next') => {
    if (!selectedShotIdForDetail) return;
    const currentIndex = filteredShots.findIndex(s => s.id === selectedShotIdForDetail);
    if (currentIndex === -1) return;
    
    if (direction === 'prev' && currentIndex > 0) {
      setSelectedShotIdForDetail(filteredShots[currentIndex - 1].id);
    } else if (direction === 'next' && currentIndex < filteredShots.length - 1) {
      setSelectedShotIdForDetail(filteredShots[currentIndex + 1].id);
    }
  };

  const handleShotChange = useCallback((id: string, field: keyof Shot, value: any) => {
    if (readOnly) return;

    onChange((prevShots) =>
      prevShots.map((s) => {
        if (s.id === id) {
          const updates: any = { [field]: value };
          if (field === "status") {
            updates.active = !isExcludedBidStatus(value);
            if (value === "Awarded" && s.originalAwardedValue === undefined) {
              updates.originalAwardedValue = calculateShotTotal(s);
              updates.originalAwardedAt = new Date().toISOString();
            }
          }
          return { ...s, ...updates };
        }
        return s;
      })
    );
  }, [readOnly, onChange, calculateShotTotal]);

  const handleDaysChange = useCallback((id: string, task: TaskType, value: string) => {
    if (readOnly) return;
    const numValue = parseFloat(value);
    onChange((prevShots) =>
      prevShots.map((s) => {
        if (s.id === id) {
          return {
            ...s,
            days: {
              ...s.days,
              [task]: isNaN(numValue) || numValue < 0 ? 0 : numValue,
            },
          };
        }
        return s;
      })
    );
  }, [readOnly, onChange]);

  const handleTextareaKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>, id: string, field: keyof Shot, rawValue: string | undefined) => {
    // Normal textarea behavior for Enter key
    // We don't need to intercept it anymore since we removed auto-bullets
  }, [handleShotChange]);

  const handleRowDoubleClick = useCallback((shot: Shot, e: React.MouseEvent) => {
    // Don't open if clicking on interactive elements
    const target = e.target as HTMLElement;
    if (
      target.tagName === "INPUT" || 
      target.tagName === "TEXTAREA" || 
      target.tagName === "BUTTON" || 
      target.closest("button") || 
      target.closest("label")
    ) {
      return;
    }
    setSelectedShotIdForDetail(shot.id);
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds(prev => {
      if (prev.size === filteredShots.length) {
        return new Set();
      } else {
        return new Set(filteredShots.map((s) => s.id));
      }
    });
  }, [filteredShots]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const formatEpisodeDisplay = (epStr: string) => {
    if (!epStr || epStr === "Unknown") return epStr;
    const match = epStr.match(/(\d+)$/);
    if (match) {
      const numStr = match[1];
      return parseInt(numStr.slice(-2), 10).toString().padStart(2, '0');
    }
    return epStr;
  };

  const [isEnriching, setIsEnriching] = useState<string | null>(null);
  const [enrichedEpisodes, setEnrichedEpisodes] = useState<Set<string>>(new Set());
  const [summaryModalDoc, setSummaryModalDoc] = useState<ProjectDocument | null>(null);

  const getScriptForEpisode = useCallback((episode: string) => {
    const formattedEp = formatEpisodeDisplay(episode);
    const epNum = parseInt(episode.match(/(\d+)$/)?.[1]?.slice(-2) || episode, 10);

    return documents.find((d) => {
      if (!d.episode) return false;
      const docEpNum = parseInt(d.episode.match(/(\d+)$/)?.[1]?.slice(-2) || d.episode, 10);
      return formatEpisodeDisplay(d.episode) === formattedEp || d.episode === episode || epNum === docEpNum;
    });
  }, [documents]);

  const getPersistedScenesFromDocument = useCallback((doc?: ProjectDocument | null) => {
    if (!doc?.analysisResult) return [];

    const scriptData = doc.analysisResult?.script_data || {};
    const sceneEntries = Array.isArray(scriptData.scenes) && scriptData.scenes.length > 0
      ? scriptData.scenes
      : Array.isArray(scriptData.scene_analysis) && scriptData.scene_analysis.length > 0
        ? scriptData.scene_analysis
        : Array.isArray(scriptData.scene_headings) && scriptData.scene_headings.length > 0
          ? scriptData.scene_headings.map((heading: string, index: number) => `${index + 1}: ${heading}`)
          : [];

    return sceneEntries
      .map((entry, index) => mapPersistedSceneEntry(entry, index))
      .filter(Boolean);
  }, []);

  const handleEnrichEpisode = async (episode: string) => {
    const script = getScriptForEpisode(episode);
    const persistedScenes = getPersistedScenesFromDocument(script);

    if (!script || (!persistedScenes.length && !script.extractedData && !script.summary && !script.vfxScenes && !script.analysisResult)) {
      toast.error("Please pull and analyze a script for this episode first in the Documents tab.");
      return;
    }

    setIsEnriching(episode);

    try {
      let normalizedScenes = persistedScenes;

      if (!normalizedScenes.length) {
        if (!script.file) {
          toast.error("Script analysis exists, but no scene-by-scene data is available yet for this episode.");
          return;
        }

        if (!process.env.GEMINI_API_KEY) {
          toast.error("Gemini API Key is missing.");
          return;
        }

        const ai = new GoogleGenAI({ apiKey: process.env.GEMINI_API_KEY });

        let mimeType = script.type;
        if (script.name.toLowerCase().endsWith('.pdf')) {
          mimeType = 'application/pdf';
        } else if (script.name.toLowerCase().endsWith('.txt')) {
          mimeType = 'text/plain';
        }

        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(script.file!);
          reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1]);
          };
          reader.onerror = error => reject(error);
        });

        const prompt = `
          Analyze this script and extract the scene descriptions for each scene.
          Return the result as a JSON object with the following structure:
          {
            "scenes": [
              {
                "sceneNumber": "The scene number (e.g., '1', '2A', '3')",
                "description": "A brief description of the scene's action or setting",
                "location": "The location of the scene (e.g., 'INT. KITCHEN')",
                "timeOfDay": "The time of day (e.g., 'DAY', 'NIGHT')"
              }
            ]
          }
        `;

        const response = await ai.models.generateContent({
          model: "gemini-3.1-pro-preview",
          contents: [
            {
              role: "user",
              parts: [
                {
                  inlineData: {
                    mimeType: mimeType,
                    data: base64,
                  },
                },
                { text: prompt },
              ],
            }
          ],
          config: {
            responseMimeType: "application/json",
            responseSchema: {
              type: Type.OBJECT,
              properties: {
                scenes: {
                  type: Type.ARRAY,
                  items: {
                    type: Type.OBJECT,
                    properties: {
                      sceneNumber: { type: Type.STRING },
                      description: { type: Type.STRING },
                      location: { type: Type.STRING },
                      timeOfDay: { type: Type.STRING }
                    }
                  }
                }
              }
            }
          }
        });

        const text = response.text || "{}";
        const parsed = JSON.parse(text);
        if (parsed.scenes && Array.isArray(parsed.scenes)) {
          normalizedScenes = parsed.scenes.map((scene: any) => normalizeScriptSceneInput(scene));
        }
      }

      if (normalizedScenes.length) {
        const updatedShots = shots.map(shot => {
          const ep = getEpisodeFromShotCode(shot.shotCode);
          if (ep === episode) {
            const sceneInfo = normalizedScenes.find((sceneInfo) => {
              const seqNum = parseInt(getSequenceFromShotCode(shot.shotCode), 10);
              const sequenceNumber = inferSequenceNumberFromScene(sceneInfo.sceneNumber, undefined, sceneInfo.sequenceNumber);
              return !isNaN(seqNum) && sequenceNumber !== undefined && seqNum === sequenceNumber;
            });

            if (sceneInfo) {
              const sceneDesc = [
                sceneInfo.locationName,
                sceneInfo.intExt,
                sceneInfo.dayNight,
                sceneInfo.description
              ].filter(Boolean).join(" - ");

              return { ...shot, sceneDescription: sceneDesc };
            }
          }
          return shot;
        });

        // We need to update the shots in the parent component
        // But ShotTable only has onUpdate which takes id, field, value
        // We can call onUpdate for each shot, or add a bulk update method.
        // For now, we'll just call onUpdate for each modified shot.
        onChange(updatedShots);
      } else {
        toast.error("No scene-by-scene script data was available to enrich this episode.");
        return;
      }

      setEnrichedEpisodes(prev => {
        const next = new Set(prev);
        next.add(episode);
        return next;
      });
    } catch (error) {
      console.error("Error enriching episode:", error);
      toast.error("Failed to enrich episode. See console for details.");
    } finally {
      setIsEnriching(null);
    }
  };

  const episodeTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    filteredShots.forEach(shot => {
      if (!isBidItemIncluded(shot)) return;
      const ep = getEpisodeFromShotCode(shot.shotCode);
      if (!totals[ep]) totals[ep] = 0;
      totals[ep] += calculateShotTotal(shot);
    });
    return totals;
  }, [filteredShots, calculateShotTotal]);

  const episodeSequenceCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredShots.forEach((shot) => {
      const episode = getEpisodeFromShotCode(shot.shotCode);
      const sequence = shot.sequenceId || getSequenceFromShotCode(shot.shotCode);
      if (!counts[episode]) counts[episode] = 0;
      if (!sequence) return;
      const bucketKey = `${episode}:${sequence}`;
      if (!(bucketKey in counts)) {
        counts[episode] += 1;
        counts[bucketKey] = 1;
      }
    });
    return counts;
  }, [filteredShots]);

  const groupedShots = useMemo(() => {
    const groups: Record<string, Shot[]> = {};
    filteredShots.forEach(shot => {
      const ep = getEpisodeFromShotCode(shot.shotCode);
      if (!groups[ep]) groups[ep] = [];
      groups[ep].push(shot);
    });
    return groups;
  }, [filteredShots]);

  const filteredTotal = filteredShots.reduce(
    (acc, shot) => acc + (isBidItemIncluded(shot) ? calculateShotTotal(shot) : 0),
    0
  );

  const taskColumnTotals = useMemo(() => {
    return filteredShots.reduce(
      (acc, shot) => {
        if (!isBidItemIncluded(shot)) {
          return acc;
        }

        const quantity = Math.max(1, shot.quantity || 1);
        const concept = (shot.days.concept || 0) * quantity;
        const dmp = (shot.days.dmp || 0) * quantity;
        const comp = (shot.days.comp || 0) * quantity;
        const roto = (shot.days.roto || 0) * quantity;
        const prep = (shot.days.prep || 0) * quantity;
        const gfx = (shot.days.gfx || 0) * quantity;
        const cameraTR = (shot.days.cameraTR || 0) * quantity;
        const objectTR = (shot.days.objectTR || 0) * quantity;
        const rANIM = (shot.days.rANIM || 0) * quantity;
        const layout = (shot.days.layout || 0) * quantity;
        const anim = (shot.days.anim || 0) * quantity;
        const light = (shot.days.light || 0) * quantity;
        const fx = (shot.days.fx || 0) * quantity;
        const supervisor =
          (concept + dmp + comp + roto + prep + gfx + cameraTR + objectTR + rANIM + layout + anim + light + fx) *
          ((setup.supervisionPercentage || 0) / 100);

        acc.frames += (shot.frames || 0) * quantity;
        acc.quantity += quantity;
        acc.cost += calculateShotTotal(shot);
        acc.supervisor += supervisor;
        acc.concept += concept;
        acc.dmp += dmp;
        acc.comp += comp;
        acc.roto += roto;
        acc.prep += prep;
        acc.gfx += gfx;
        acc.cameraTR += cameraTR;
        acc.objectTR += objectTR;
        acc.rANIM += rANIM;
        acc.layout += layout;
        acc.anim += anim;
        acc.light += light;
        acc.fx += fx;
        return acc;
      },
      {
        cost: 0,
        frames: 0,
        quantity: 0,
        supervisor: 0,
        concept: 0,
        dmp: 0,
        comp: 0,
        roto: 0,
        prep: 0,
        gfx: 0,
        cameraTR: 0,
        objectTR: 0,
        rANIM: 0,
        layout: 0,
        anim: 0,
        light: 0,
        fx: 0,
      }
    );
  }, [filteredShots, setup.supervisionPercentage]);

  const formatTaskSubtotalValue = (value: number) => {
    if (!Number.isFinite(value) || value === 0) return "";
    const rounded = Math.round(value * 100) / 100;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2).replace(/\.?0+$/, "");
  };

  useEffect(() => {
    onFilterStateChange?.({
      isActive: Boolean(deferredSearch.trim() || advancedFilters.length > 0 || isStatusFilterActive || showCodeIssuesOnly),
      filteredIds: filteredShots.map((shot) => shot.id),
      search: deferredSearch,
      advancedFilterCount: advancedFilters.length + (isStatusFilterActive ? 1 : 0) + (showCodeIssuesOnly ? 1 : 0),
      visibleCount: filteredShots.length,
      totalCount: shots.length,
    });
  }, [onFilterStateChange, deferredSearch, advancedFilters.length, filteredShots, shots.length, isStatusFilterActive, showCodeIssuesOnly]);

  const handleManualSort = () => {
    onChange(sortShots(shots));
  };

  const handleBulkApply = (updates: any) => {
    if (readOnly) return;
    const updatedShots = shots.map((s) => {
      if (selectedIds.has(s.id)) {
        const newDays = { ...s.days };
        (["concept", "dmp", "comp", "roto", "prep", "gfx", "cameraTR", "objectTR", "rANIM", "layout", "anim", "light", "fx"] as TaskType[]).forEach((task) => {
          if (updates[task] !== undefined) {
            newDays[task] = updates[task];
          }
        });
        return {
          ...s,
          days: newDays,
          assumptions: updates.assumptions !== undefined ? updates.assumptions : s.assumptions,
          internalNotes: updates.internalNotes !== undefined ? updates.internalNotes : s.internalNotes,
          status: updates.status !== undefined ? updates.status : s.status,
          active: updates.status !== undefined ? !isExcludedBidStatus(updates.status) : s.active,
        };
      }
      return s;
    });
    onChange(updatedShots);
    setIsBulkEditOpen(false);
    setSelectedIds(new Set());
  };

  const handleBulkDelete = () => {
    if (readOnly) return;
    const updatedShots = shots.filter(s => !selectedIds.has(s.id));
    onChange(updatedShots);
    setIsBulkEditOpen(false);
    setSelectedIds(new Set());
  };

  const handleThumbnailUpload = useCallback((id: string, e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const targetWidth = 480;
          const targetHeight = 270;
          canvas.width = targetWidth;
          canvas.height = targetHeight;
          const ctx = canvas.getContext("2d");

          if (ctx) {
            // Calculate cropping to maintain aspect ratio (cover)
            const imgAspectRatio = img.width / img.height;
            const targetAspectRatio = targetWidth / targetHeight;
            let drawWidth, drawHeight, offsetX, offsetY;

            if (imgAspectRatio > targetAspectRatio) {
              // Image is wider than target
              drawHeight = targetHeight;
              drawWidth = img.width * (targetHeight / img.height);
              offsetX = (targetWidth - drawWidth) / 2;
              offsetY = 0;
            } else {
              // Image is taller than target
              drawWidth = targetWidth;
              drawHeight = img.height * (targetWidth / img.width);
              offsetX = 0;
              offsetY = (targetHeight - drawHeight) / 2;
            }

            ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
            const croppedDataUrl = canvas.toDataURL("image/jpeg", 0.85);
            handleShotChange(id, "thumbnail", croppedDataUrl);
          }
        };
        img.src = event.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  }, [readOnly, handleShotChange]);

  const shotIndices = useMemo(() => {
    const map = new Map<string, number>();
    filteredShots.forEach((s, i) => map.set(s.id, i));
    return map;
  }, [filteredShots]);

  const shotIds = useMemo(() => filteredShots.map(s => s.id), [filteredShots]);

  return (
    <div className="flex flex-col h-full">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col h-full">
        <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between gap-4 shrink-0">
          <div className="flex items-center gap-3">
            <SectionPillHeader icon={Film} title="Shot Bidding" />
          </div>

          <div className="flex items-center gap-3 flex-1 justify-end">
            {selectedIds.size > 0 && !readOnly && (
              <button
                onClick={() => setIsBulkEditOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400 text-xs font-medium rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
              >
                <Edit3 className="w-3.5 h-3.5" />
                Bulk Edit ({selectedIds.size})
              </button>
            )}
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                type="text"
                placeholder="Search shots..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
              />
            </div>
            <button
              onClick={() => setBiddingMode(!biddingMode)}
              className={cn(
                "group relative flex items-center justify-center w-8 h-8 rounded-lg border transition-colors",
                biddingMode 
                  ? "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400" 
                  : "bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
              )}
              title="Bidding View"
            >
              <FoldVertical className={cn("w-3.5 h-3.5", biddingMode && "fill-current")} />
            </button>
            <button
              onClick={handleManualSort}
              className="group relative flex items-center justify-center w-8 h-8 rounded-lg border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              title="Sort Shots"
            >
              <ArrowUpDown className="w-3.5 h-3.5" />
            </button>
            <div className="relative" ref={statusFilterRef}>
              <button
                onClick={() => setIsStatusFilterOpen((prev) => !prev)}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors border",
                  isStatusFilterOpen || isStatusFilterActive
                    ? "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800"
                    : "bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700"
                )}
                title="Filter by status"
              >
                <Filter className="w-3.5 h-3.5" />
                <span>Status</span>
                {isStatusFilterActive && (
                  <span className="rounded-full bg-white/80 dark:bg-slate-900/80 px-1.5 py-0.5 text-[10px] leading-none">
                    {selectedStatuses.length}
                  </span>
                )}
                <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", isStatusFilterOpen && "rotate-180")} />
              </button>
              {isStatusFilterOpen && (
                <div className="absolute right-0 top-full z-30 mt-2 w-52 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg p-2">
                  <div className="flex items-center justify-between px-2 py-1.5 text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-[0.16em]">
                    <span>Status Filter</span>
                    <button
                      onClick={selectAllStatuses}
                      className="text-[10px] font-medium tracking-normal normal-case text-indigo-600 dark:text-indigo-300 hover:text-indigo-700 dark:hover:text-indigo-200"
                    >
                      Select all
                    </button>
                  </div>
                  <div className="mt-1 flex flex-col">
                    {BID_ITEM_STATUSES.map((status) => {
                      const checked = selectedStatusSet.has(status);
                      return (
                        <label
                          key={status}
                          className={cn(
                            "flex items-center gap-2 rounded-lg px-2 py-2 text-xs border cursor-pointer transition-colors",
                            getBidStatusClasses(status),
                            checked ? "ring-1 ring-inset ring-current/15" : "opacity-85 hover:opacity-100"
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleStatusFilter(status)}
                            className="h-3.5 w-3.5 rounded border-slate-300 text-slate-500 focus:ring-slate-400"
                          />
                          <span>{status}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={() => setShowCodeIssuesOnly((prev) => !prev)}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors border",
                showCodeIssuesOnly
                  ? "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800"
                  : "bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700"
              )}
              title="Show only malformed shot codes"
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>Code Issues</span>
              {invalidShotCodeCount > 0 && (
                <span className="rounded-full bg-white/80 dark:bg-slate-900/80 px-1.5 py-0.5 text-[10px] leading-none">
                  {invalidShotCodeCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
              className={cn(
                "flex items-center justify-center w-8 h-8 rounded-lg transition-colors border",
                isFilterPanelOpen || advancedFilters.length > 0
                  ? "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400 border-indigo-200 dark:border-amber-800"
                  : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700"
              )}
              title={advancedFilters.length > 0 ? `Filters (${advancedFilters.length})` : ""}
            >
              <Filter className="w-3.5 h-3.5" />
            </button>
            {!readOnly && (
              <button
                onClick={onAddShot}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#ffcc01] text-slate-900 text-xs font-medium rounded-lg hover:bg-[#e6b801] transition-colors shadow-sm"
              >
                <Plus className="w-4 h-4" />
                Add Shot
              </button>
            )}
          </div>
        </div>

        {(isFilterPanelOpen || advancedFilters.length > 0) && (
          <div className="bg-slate-50 dark:bg-slate-950/60 rounded-lg p-3 border border-slate-200 dark:border-slate-800 animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 dark:text-slate-200">
                  <Filter className="w-3.5 h-3.5" />
                  Advanced Filters
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={clearFilters}
                    className="text-[10px] text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-300 font-medium transition-colors"
                  >
                    Clear All
                  </button>
                  <button
                    onClick={() => setIsFilterPanelOpen(false)}
                    className="p-1 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-200 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                {advancedFilters.map((filter) => (
                  <div key={filter.id} className="flex flex-wrap items-center gap-2 bg-white dark:bg-slate-900 p-1.5 rounded-md border border-slate-200 dark:border-slate-700 shadow-sm">
                    <select
                      value={filter.field}
                      onChange={(e) => updateFilter(filter.id, { field: e.target.value })}
                      className="text-[10px] border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                      {FILTER_FIELDS.map((f) => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                      ))}
                    </select>
                    <select
                      value={filter.operator}
                      onChange={(e) => updateFilter(filter.id, { operator: e.target.value as any })}
                      className="text-[10px] border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                      {OPERATORS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    {!['is_empty', 'is_not_empty'].includes(filter.operator) && (
                      <input
                        type="text"
                        value={filter.value}
                        onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
                        placeholder="Value..."
                        className="text-[10px] border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-indigo-500 flex-1 min-w-[80px]"
                      />
                    )}
                    <button
                      onClick={() => removeFilter(filter.id)}
                      className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-3 pt-1.5 border-t border-slate-200 dark:border-slate-800">
                <button
                  onClick={addFilter}
                  className="flex items-center gap-1 text-[10px] font-medium text-indigo-600 hover:text-indigo-700 transition-colors whitespace-nowrap"
                >
                  <Plus className="w-3 h-3" />
                  Add Filter
                </button>
                <div className="h-3 w-px bg-slate-200 dark:bg-slate-800 hidden sm:block" />
                <div className="relative flex-1 w-full">
                  <Sparkles className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-indigo-500" />
                  <input
                    type="text"
                    placeholder="Smart Filter (e.g., 'Active shots with > 10 days')"
                    value={smartFilter}
                    onChange={(e) => setSmartFilter(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSmartFilter()}
                    className="w-full pl-7 pr-4 py-1 text-[10px] border border-indigo-200 dark:border-indigo-800 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-indigo-50/50 dark:bg-indigo-950/30 text-slate-700 dark:text-slate-200"
                  />
                  {isSmartFiltering && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2">
                      <div className="w-3 h-3 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={scrollContainerRef} className="flex-1 overflow-auto pb-4">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <table className="w-full text-sm text-left table-fixed border-separate border-spacing-0" style={{ minWidth: visibleWidth }}>
            <thead className="text-xs text-slate-500 dark:text-slate-400 uppercase bg-slate-50 dark:bg-slate-950 sticky top-0 z-20 whitespace-nowrap">
            <tr>
              <ResizableTh 
                id="select" 
                width={colWidths.select} 
                onResizeStart={handleResizeStart} 
                className="px-4 py-3 sticky left-0 z-30 bg-slate-50 dark:bg-slate-950 border-l-2 border-r-2 border-b-2 border-white dark:border-slate-900"
                style={{ left: 0 }}
              >
                <button onClick={toggleSelectAll} className="flex w-full items-center justify-center text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-200" disabled={readOnly}>
                  {selectedIds.size === filteredShots.length && filteredShots.length > 0 ? (
                    <CheckSquare className="w-4 h-4" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                </button>
              </ResizableTh>
              <ResizableTh 
                id="active" 
                width={colWidths.active} 
                onResizeStart={handleResizeStart} 
                className="px-4 py-3 text-center sticky z-30 bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900"
                style={{ left: colWidths.select }}
              >
                Status
              </ResizableTh>
              <ResizableTh 
                id="thumb" 
                width={colWidths.thumb} 
                onResizeStart={handleResizeStart} 
                className="px-4 py-3 text-center sticky z-30 bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900"
                style={{ left: colWidths.select + colWidths.active }}
              >
                Thumbnail
              </ResizableTh>
              <ResizableTh 
                id="shotCode" 
                width={colWidths.shotCode} 
                onResizeStart={handleResizeStart} 
                className="px-4 py-3 sticky z-30 bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900"
                style={{ left: colWidths.select + colWidths.active + colWidths.thumb }}
              >
                Shot Code
              </ResizableTh>
              <ResizableTh 
                id="total" 
                width={colWidths.total} 
                onResizeStart={handleResizeStart} 
                className="px-4 py-3 text-right sticky z-30 bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900"
                style={{ left: colWidths.select + colWidths.active + colWidths.thumb + colWidths.shotCode }}
              >
                Cost
              </ResizableTh>
              {!biddingMode && (
                <>
                  <ResizableTh id="description" width={colWidths.description} onResizeStart={handleResizeStart} className="px-4 py-3 border-r-2 border-b-2 border-white dark:border-slate-900">Description</ResizableTh>
                  <ResizableTh id="frames" width={colWidths.frames} onResizeStart={handleResizeStart} className="px-4 py-3 text-center border-r-2 border-b-2 border-white dark:border-slate-900">Frames</ResizableTh>
                  <ResizableTh id="qty" width={colWidths.qty} onResizeStart={handleResizeStart} className="px-4 py-3 text-center border-r-2 border-b-2 border-white dark:border-slate-900">Qty</ResizableTh>
                </>
              )}
              <ResizableTh id="supervisor" width={colWidths.supervisor} onResizeStart={handleResizeStart} className="px-4 py-3 text-center bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900" title="Supervision">Sup</ResizableTh>
              <ResizableTh 
                id="concept" 
                width={colWidths.concept} 
                onResizeStart={handleResizeStart} 
                className={cn(
                  "px-1 py-3 text-center bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900",
                  biddingMode && "border-l-2"
                )} 
                title="Concept"
              >
                Concept
              </ResizableTh>
              <ResizableTh id="dmp" width={colWidths.dmp} onResizeStart={handleResizeStart} className="px-1 py-3 text-center bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900" title="DMP">DMP</ResizableTh>
              <ResizableTh id="comp" width={colWidths.comp} onResizeStart={handleResizeStart} className="px-1 py-3 text-center bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900" title="Compositing">Comp</ResizableTh>
              <ResizableTh id="roto" width={colWidths.roto} onResizeStart={handleResizeStart} className="px-1 py-3 text-center bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900" title="Roto">Roto</ResizableTh>
              <ResizableTh id="prep" width={colWidths.prep} onResizeStart={handleResizeStart} className="px-1 py-3 text-center bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900" title="Prep / Paint">Prep</ResizableTh>
              <ResizableTh id="gfx" width={colWidths.gfx} onResizeStart={handleResizeStart} className="px-1 py-3 text-center bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900" title="GFX">GFX</ResizableTh>
              <ResizableTh id="cameraTR" width={colWidths.cameraTR} onResizeStart={handleResizeStart} className="px-1 py-3 text-center bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900" title="CameraTR">CamTR</ResizableTh>
              <ResizableTh id="objectTR" width={colWidths.objectTR} onResizeStart={handleResizeStart} className="px-1 py-3 text-center bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900" title="ObjectTR">ObjTR</ResizableTh>
              <ResizableTh id="rANIM" width={colWidths.rANIM} onResizeStart={handleResizeStart} className="px-1 py-3 text-center bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900" title="rANIM">rANIM</ResizableTh>
              <ResizableTh id="layout" width={colWidths.layout} onResizeStart={handleResizeStart} className="px-1 py-3 text-center bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900" title="Layout">Layout</ResizableTh>
              <ResizableTh id="anim" width={colWidths.anim} onResizeStart={handleResizeStart} className="px-1 py-3 text-center bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900" title="Anim">Anim</ResizableTh>
              <ResizableTh id="light" width={colWidths.light} onResizeStart={handleResizeStart} className="px-1 py-3 text-center bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900" title="Lighting">Light</ResizableTh>
              <ResizableTh id="fx" width={colWidths.fx} onResizeStart={handleResizeStart} className="px-1 py-3 text-center bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900" title="FX">FX</ResizableTh>
              {!biddingMode && (
                <>
                  <ResizableTh id="assumptions" width={colWidths.assumptions} onResizeStart={handleResizeStart} className="px-4 py-3 border-r-2 border-b-2 border-white dark:border-slate-900">Assumptions</ResizableTh>
                  <ResizableTh id="scopeOfWork" width={colWidths.scopeOfWork} onResizeStart={handleResizeStart} className="px-4 py-3 border-r-2 border-b-2 border-white dark:border-slate-900">Scope of Work</ResizableTh>
                  <ResizableTh id="internalNotes" width={colWidths.internalNotes} onResizeStart={handleResizeStart} className="px-4 py-3 border-r-2 border-b-2 border-white dark:border-slate-900">Internal Notes</ResizableTh>
                </>
              )}
              {!readOnly && <ResizableTh id="actions" width={colWidths.actions} onResizeStart={handleResizeStart} className="px-4 py-3 text-center border-r-2 border-b-2 border-white dark:border-slate-900">Actions</ResizableTh>}
            </tr>
          </thead>
          <SortableContext
            items={shotIds}
            strategy={verticalListSortingStrategy}
          >
            {Object.entries(groupedShots).map(([currentEpisode, episodeShots]: [string, Shot[]]) => {
              const episodeTotal = episodeTotals[currentEpisode] || 0;
              const formattedEp = formatEpisodeDisplay(currentEpisode);
              const epNum = parseInt(currentEpisode.match(/(\d+)$/)?.[1]?.slice(-2) || currentEpisode, 10);
              
              const scriptForEpisode = getScriptForEpisode(currentEpisode);
              const persistedSceneCount = getPersistedScenesFromDocument(scriptForEpisode).length;
              const hasEpisodeScriptData = Boolean(
                scriptForEpisode && (
                  persistedSceneCount > 0 ||
                  scriptForEpisode.analysisResult ||
                  scriptForEpisode.extractedData ||
                  scriptForEpisode.summary ||
                  scriptForEpisode.vfxScenes ||
                  scriptForEpisode.file
                )
              );
              const isEnriched = enrichedEpisodes.has(currentEpisode);
              const isCollapsed = collapsedEpisodes.has(currentEpisode);

              return (
                <tbody key={currentEpisode} className="divide-y divide-slate-100 dark:divide-slate-800">
                  <tr className="bg-slate-50/80 dark:bg-slate-950/70">
                    <th colSpan={totalCols} className="px-0 py-0 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider bg-slate-50 dark:bg-slate-950 sticky top-[42px] z-[5] shadow-sm text-left border-b border-slate-200 dark:border-slate-800">
                      <div className="relative min-h-[42px] min-w-full bg-slate-50/90 dark:bg-slate-950/90 backdrop-blur-sm">
                        <div className="sticky left-0 z-[6] grid h-[42px] w-fit min-w-[420px] grid-cols-[32px_176px_112px_100px] items-center gap-1 px-4 bg-slate-50/95 dark:bg-slate-950/95 backdrop-blur-sm">
                          <button 
                            onClick={() => toggleEpisodeCollapse(currentEpisode)}
                            className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors text-slate-400 dark:text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-300"
                            title={isCollapsed ? "Expand Episode" : "Collapse Episode"}
                          >
                            {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                          <span className="min-w-0 truncate">Episode {formattedEp} ({episodeShots.length} {episodeShots.length === 1 ? 'shot' : 'shots'})</span>
                          <span className="inline-flex items-center w-fit px-2 py-1 rounded-md bg-slate-100 dark:bg-slate-800 text-[11px] font-semibold text-slate-500 dark:text-slate-400">
                            {episodeSequenceCounts[currentEpisode] || 0} Sequences
                          </span>
                          <div className="flex h-full min-w-0 items-center justify-end gap-2 text-right font-sans text-sm font-bold text-slate-900 dark:text-slate-100">
                            <span className="whitespace-nowrap">{formatCurrency(episodeTotal, setup.currency)}</span>
                            {setup.targetCurrency && setup.exchangeRate > 0 && (
                              <span className="whitespace-nowrap text-xs font-medium text-slate-400 dark:text-slate-500">
                                ({formatCurrency(episodeTotal * setup.exchangeRate, setup.targetCurrency)})
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="sticky right-0 z-[6] ml-auto -mt-[42px] flex h-[42px] w-fit max-w-[35%] items-center justify-end gap-2 px-4">
                          {isEnriched && scriptForEpisode?.sceneCount !== undefined && (
                            <span className="inline-flex items-center px-2 py-1 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-300 rounded-md text-xs font-bold whitespace-nowrap">
                              {scriptForEpisode.sceneCount} Scenes
                            </span>
                          )}
                        </div>
                      </div>
                    </th>
                  </tr>
                  {!isCollapsed && episodeShots.map((shot, index) => {
                    const previousShot = index > 0 ? episodeShots[index - 1] : null;
                    const currentSequence = shot.sequenceId || getSequenceFromShotCode(shot.shotCode);
                    const previousSequence = previousShot ? (previousShot.sequenceId || getSequenceFromShotCode(previousShot.shotCode)) : null;
                    const showSequenceDivider = Boolean(previousShot && currentSequence && previousSequence && currentSequence !== previousSequence);

                    return (
                      <React.Fragment key={shot.id}>
                        {showSequenceDivider && (
                          <tr aria-hidden="true" className="bg-transparent">
                            <td colSpan={totalCols} className="border-t border-slate-200 dark:border-slate-700 px-0 py-0" />
                          </tr>
                        )}
                        <SortableRow
                          shot={shot}
                          index={shotIndices.get(shot.id) || 0}
                          calculateShotTotal={calculateShotTotal}
                          isSelected={selectedIds.has(shot.id)}
                          toggleSelect={toggleSelect}
                          handleShotChange={handleShotChange}
                          handleDaysChange={handleDaysChange}
                          handleThumbnailUpload={handleThumbnailUpload}
                          handleRowDoubleClick={handleRowDoubleClick}
                          handleTextareaKeyDown={handleTextareaKeyDown}
                          onDuplicateShot={onDuplicateShot}
                          onDeleteShot={onDeleteShot}
                          readOnly={readOnly}
                          colWidths={colWidths}
                          setup={setup}
                          rates={rates}
                          biddingMode={biddingMode}
                        />
                      </React.Fragment>
                    );
                  })}
                </tbody>
              );
            })}
            {filteredShots.length === 0 && (
              <tbody>
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                    <div className="sticky left-0 w-full text-center">
                      No shots found. Add a shot to get started.
                    </div>
                  </td>
                </tr>
              </tbody>
            )}
            {filteredShots.length > 0 && (
              <tbody>
                <tr className="bg-slate-50/70 dark:bg-slate-950/40">
                  <td className="px-4 py-2 sticky left-0 z-[2] bg-inherit border-r-2 border-t-2 border-white dark:border-slate-900" style={{ left: 0 }} />
                  <td className="px-4 py-2 sticky z-[2] bg-inherit border-r-2 border-t-2 border-white dark:border-slate-900" style={{ left: colWidths.select }} />
                  <td className="px-4 py-2 sticky z-[2] bg-inherit border-r-2 border-t-2 border-white dark:border-slate-900" style={{ left: colWidths.select + colWidths.active }} />
                  <td
                    className="px-4 py-2 sticky z-[2] bg-inherit border-r-2 border-t-2 border-white dark:border-slate-900 text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300"
                    style={{ left: colWidths.select + colWidths.active + colWidths.thumb }}
                  >
                    Column Totals
                  </td>
                  <td
                    className="px-4 py-2 text-right sticky z-[2] bg-inherit border-r-2 border-t-2 border-white dark:border-slate-900 text-xs font-semibold text-slate-900 dark:text-slate-100"
                    style={{ left: colWidths.select + colWidths.active + colWidths.thumb + colWidths.shotCode }}
                  >
                    <div>{formatCurrency(taskColumnTotals.cost, setup.currency)}</div>
                    {setup.targetCurrency && setup.exchangeRate > 0 && (
                      <div className="text-slate-400 dark:text-slate-500 mt-0.5 text-xs">
                        {formatCurrency(taskColumnTotals.cost * setup.exchangeRate, setup.targetCurrency)}
                      </div>
                    )}
                  </td>
                  {!biddingMode && (
                    <>
                      <td className="px-4 py-2 border-r-2 border-t-2 border-white dark:border-slate-900" />
                      <td className="px-2 py-2 border-r-2 border-t-2 border-white dark:border-slate-900" />
                      <td className="px-2 py-2 border-r-2 border-t-2 border-white dark:border-slate-900" />
                    </>
                  )}
                  {(["supervisor", "concept", "dmp", "comp", "roto", "prep", "gfx", "cameraTR", "objectTR", "rANIM", "layout", "anim", "light", "fx"] as TaskType[]).map((task, i) => (
                    <td
                      key={task}
                      className={cn(
                        "px-1 py-2 text-center bg-slate-50 dark:bg-slate-800/80 border-r-2 border-t-2 border-white dark:border-slate-900 text-xs font-semibold text-slate-700 dark:text-slate-200",
                        (i === 0 || (biddingMode && task === "concept")) && "border-l-2"
                      )}
                    >
                      {formatTaskSubtotalValue(taskColumnTotals[task])}
                    </td>
                  ))}
                  {!biddingMode && (
                    <>
                      <td className="px-4 py-2 border-r-2 border-t-2 border-white dark:border-slate-900" />
                      <td className="px-4 py-2 border-r-2 border-t-2 border-white dark:border-slate-900" />
                      <td className="px-4 py-2 border-r-2 border-t-2 border-white dark:border-slate-900" />
                    </>
                  )}
                  {!readOnly && <td className="px-4 py-2 border-r-2 border-t-2 border-white dark:border-slate-900" />}
                </tr>
              </tbody>
            )}
          </SortableContext>
        </table>
      </DndContext>
      </div>

      <div className="bg-slate-50 dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800 py-1.5 px-4 flex flex-col items-end gap-3 z-20">
        <div className="flex justify-end items-center gap-6 w-full">
          <div className="text-right font-semibold text-slate-500 dark:text-slate-400 text-xs uppercase tracking-wider">
            Subtotal (Active Status)
          </div>
          <div className="text-right font-bold text-slate-900 dark:text-slate-100">
            <div className="text-sm">{formatCurrency(filteredTotal, setup.currency)}</div>
            {setup.targetCurrency && setup.exchangeRate > 0 && (
              <div className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                {formatCurrency(filteredTotal * setup.exchangeRate, setup.targetCurrency)}
              </div>
            )}
          </div>
        </div>
      </div>
      </div>

      {isBulkEditOpen && !readOnly && (
        <BulkEditModal
          onClose={() => setIsBulkEditOpen(false)}
          onApply={handleBulkApply}
          onDelete={handleBulkDelete}
          selectedCount={selectedIds.size}
        />
      )}

      {selectedShotForDetail && (
        <ShotDetailModal
          shot={selectedShotForDetail}
          isOpen={!!selectedShotForDetail}
          onClose={() => setSelectedShotIdForDetail(null)}
          onUpdate={handleShotChange}
          onNavigate={handleNavigateDetail}
          hasPrev={filteredShots.findIndex(s => s.id === selectedShotIdForDetail) > 0}
          hasNext={filteredShots.findIndex(s => s.id === selectedShotIdForDetail) < filteredShots.length - 1}
          readOnly={readOnly}
          rates={rates}
          setup={setup}
          documents={documents}
          scenes={scenes}
        />
      )}

      {isCreatorOpen && !readOnly && (
        <ShotCodeGenerator
          isOpen={isCreatorOpen}
          onClose={() => setIsCreatorOpen(false)}
          setup={setup}
          shots={shots}
          onGenerate={(newShots, updateExisting, newShowCode) => {
            let updatedShots = [...shots];
            
            if (updateExisting) {
              updatedShots = updatedShots.map(shot => {
                const parts = shot.shotCode.split('_');
                let epStr, seqStr, shotStr;
                
                if (parts.length === 4) {
                  epStr = parts[1];
                  seqStr = parts[2];
                  shotStr = parts[3];
                } else if (parts.length === 3) {
                  epStr = parts[0];
                  seqStr = parts[1];
                  shotStr = parts[2];
                } else {
                  return shot;
                }
                
                const finalShowCode = newShowCode !== undefined ? newShowCode : setup.showCode;
                const newShotCode = finalShowCode 
                  ? `${finalShowCode}_${epStr}_${seqStr}_${shotStr}`
                  : `${epStr}_${seqStr}_${shotStr}`;
                  
                return { ...shot, shotCode: newShotCode };
              });
            }
            
            const existingCodes = new Set(updatedShots.map(s => s.shotCode));
            const uniqueNewShots = newShots.filter(s => !existingCodes.has(s.shotCode));
            
            onChange(sortShots([...updatedShots, ...uniqueNewShots]));
          }}
          onUpdateSetup={onUpdateSetup}
        />
      )}
      {summaryModalDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col border border-slate-200 dark:border-slate-800">
            <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-950 rounded-t-xl">
              <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-indigo-500" />
                Episode Summary: {summaryModalDoc.episode || summaryModalDoc.name}
              </h2>
              <button
                onClick={() => setSummaryModalDoc(null)}
                className="p-2 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto">
              {summaryModalDoc.extractedData ? (
                <div className="space-y-6">
                  {summaryModalDoc.extractedData.map((item, idx) => (
                    <div key={idx} className="bg-white dark:bg-slate-900 p-4 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">
                      <h5 className="text-sm font-bold text-slate-800 dark:text-slate-100 mb-2 flex items-center">
                        <Sparkles className="w-4 h-4 mr-2 text-indigo-500" />
                        {item.prompt}
                      </h5>
                      <div className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{item.answer}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {summaryModalDoc.summary && (
                    <div className="bg-white dark:bg-slate-900 p-4 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">
                      <h5 className="text-sm font-bold text-slate-800 dark:text-slate-100 mb-2 flex items-center">
                        <Sparkles className="w-4 h-4 mr-2 text-indigo-500" />
                        Episode Overview
                      </h5>
                      <div className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{summaryModalDoc.summary}</div>
                    </div>
                  )}
                  {summaryModalDoc.vfxScenes && (
                    <div className="bg-indigo-50 dark:bg-indigo-950/30 p-4 rounded-lg border border-indigo-100 dark:border-indigo-900/50 shadow-sm">
                      <h5 className="text-sm font-bold text-indigo-800 dark:text-indigo-200 mb-2 flex items-center">
                        <Sparkles className="w-4 h-4 mr-2 text-indigo-500" />
                        VFX Scenes Extraction
                      </h5>
                      <div className="text-sm text-indigo-900 dark:text-indigo-100 whitespace-pre-wrap">{summaryModalDoc.vfxScenes}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
